import { db } from './firebaseConfig';
import { 
  collection, 
  query, 
  where, 
  doc, 
  getDoc, 
  getDocs, 
  onSnapshot,
  Timestamp,
  deleteDoc,
  orderBy,
  limit
} from 'firebase/firestore';
import { 
  Conversation, 
  Message, 
  FirebaseConversationData,
  FirebaseMessageData,
  ConversationPaginationResult
} from '../models/Message';

/**
 * Helper function to safely convert a Firestore timestamp to ISO string
 * Handles different types of timestamp formats:
 * - Firebase Timestamp objects with toDate() method
 * - Objects with seconds and nanoseconds
 * - Strings in ISO format (including those with +00:00 UTC suffix)
 * - Numbers (milliseconds since epoch)
 * 
 * @param timestamp - The timestamp to convert
 * @returns ISO string representation of the date
 * @throws Error if the timestamp cannot be converted
 */
const safeTimestampToISOString = (timestamp: any): string => {
  // console.log('safeTimestampToISOString input:', timestamp, typeof timestamp);
  
  // If timestamp is already a string, process it appropriately
  if (typeof timestamp === 'string') {
    // console.log('Timestamp is already a string:', timestamp);
    
    // Check if it's a Firebase format with +00:00
    if (timestamp.includes('+00:00')) {
      // Extract date parts from format like: 2025-03-19T13:45:16.317000+00:00
      const match = timestamp.match(/(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d+)\+00:00/);
      
      if (match) {
        const [_, datePart, hours, minutes, seconds, fractionalSeconds] = match;
        
        // Only use the first 3 digits of fractional seconds (milliseconds)
        const milliseconds = fractionalSeconds.substring(0, 3);
        
        // Format as ISO string with Z suffix
        const formattedDate = `${datePart}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
        // console.log('Reformatted Firebase timestamp string:', formattedDate);
        return formattedDate;
      }
    }
    
    // If it's already in the right format or doesn't match our pattern, return as is
    return timestamp;
  }
  
  if (!timestamp) {
    throw new Error('Missing timestamp value');
  }
  
  try {
    // Case 1: Firebase Timestamp with toDate method
    if (typeof timestamp.toDate === 'function') {
      const result = timestamp.toDate().toISOString();
      return result;
    }
    
    // Case 2: Object with seconds and nanoseconds (typical Firestore timestamp)
    if (timestamp.seconds !== undefined && typeof timestamp.seconds === 'number') {
      const milliseconds = timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000;
      const result = new Date(milliseconds).toISOString();
      return result;
    }
    
    // Case 3: Number (milliseconds since epoch)
    if (typeof timestamp === 'number') {
      const result = new Date(timestamp).toISOString();
      return result;
    }
    
    // Unrecognized format
    throw new Error(`Unrecognized timestamp format: ${JSON.stringify(timestamp)}`);
  } catch (e) {
    console.error('Failed to convert timestamp:', e, timestamp);
    throw e; // Re-throw the error
  }
};


/**
 * Fetches all conversations for a user from the new user-centric collections.
 * 
 * @param userId - The ID of the user
 * @returns Promise containing an array of conversations
 */
export const fetchUserConversations = async (userId: string): Promise<Conversation[]> => {
  console.log('fetchUserConversations', userId);
  try {
    const conversations: Conversation[] = [];

    // 1. Fetch reflection conversations
    const reflectionConversationsRef = collection(db, `users/${userId}/reflection_conversations`);
    const reflectionSnapshot = await getDocs(reflectionConversationsRef);

    for (const conversationDoc of reflectionSnapshot.docs) {
      const conversation = await fetchConversationWithMessages(
        userId, 
        conversationDoc.id, 
        'reflection'
      );
      if (conversation) {
        conversations.push(conversation);
      }
    }

    // 2. Fetch fraktal conversations
    const fraktalConversationsRef = collection(db, `users/${userId}/fraktal_conversations`);
    const fraktalSnapshot = await getDocs(fraktalConversationsRef);

    for (const conversationDoc of fraktalSnapshot.docs) {
      const conversation = await fetchConversationWithMessages(
        userId, 
        conversationDoc.id, 
        'fraktal'
      );
      if (conversation) {
        conversations.push(conversation);
      }
    }

    // 3. Fetch prompt-graph conversations
    const graphConversationsRef = collection(db, `users/${userId}/graph_conversations`);
    const graphSnapshot = await getDocs(graphConversationsRef);

    for (const conversationDoc of graphSnapshot.docs) {
      const conversation = await fetchConversationWithMessages(
        userId, 
        conversationDoc.id, 
        'prompt-graph'
      );
      if (conversation) {
        conversations.push(conversation);
      }
    }

    // 4. Sort by last message timestamp
    return conversations.sort(
      (a, b) => new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime()
    );
  } catch (error) {
    console.error('Error fetching user conversations:', error);
    throw error;
  }
};

/**
 * Fetches a single conversation with its messages.
 * 
 * @param userId - The ID of the user
 * @param conversationId - The ID of the conversation
 * @param type - The type of conversation ('reflection' | 'fraktal' | 'prompt-graph')
 * @returns Promise containing the conversation with messages or null if not found
 */
export const fetchConversationWithMessages = async (
  userId: string,
  conversationId: string,
  type: 'reflection' | 'fraktal' | 'prompt-graph'
): Promise<Conversation | null> => {
  console.log('fetchConversationWithMessages', userId, conversationId, type);
  try {
    let conversationType: string;
    if (type === 'fraktal') {
      conversationType = 'fraktal_conversations';
    } else if (type === 'prompt-graph') {
      conversationType = 'graph_conversations';
    } else {
      conversationType = 'reflection_conversations';
    }
    
    const conversationPath = `users/${userId}/${conversationType}/${conversationId}`;
    const conversationRef = doc(db, conversationPath);
    const conversationSnap = await getDoc(conversationRef);

    if (!conversationSnap.exists()) {
      return null;
    }

    const conversationData = conversationSnap.data() as FirebaseConversationData;
    
    // Fetch messages for this conversation
    const messages = await fetchConversationMessages(userId, conversationId, type);
    
    // Convert Firebase timestamps to ISO strings
    let lastMessageTimestamp = '';
    let createdAt = '';
    let updatedAt = '';
    
    try {
      // Get the lastMessageTimestamp from lastMessageTimestamp or the last message
      if (conversationData.lastMessageTimestamp) {
        lastMessageTimestamp = safeTimestampToISOString(conversationData.lastMessageTimestamp);
      } else if (messages.length > 0) {
        lastMessageTimestamp = messages[messages.length - 1].createdAt;
      } else {
        throw new Error('No last message timestamp available');
      }
      
      // Get createdAt from createdAt
      if (conversationData.createdAt) {
        createdAt = safeTimestampToISOString(conversationData.createdAt);
      } else {
        throw new Error(`Conversation ${conversationId} is missing createdAt field`);
      }
      
      // Get updatedAt from updatedAt
      if (conversationData.updatedAt) {
        updatedAt = safeTimestampToISOString(conversationData.updatedAt);
      } else {
        throw new Error(`Conversation ${conversationId} is missing updatedAt field`);
      }
    } catch (error) {
      console.error('Error converting timestamps:', error);
      throw error;
    }

    // Create the conversation object using the improved structure
    const conversation: Conversation = {
      id: conversationId,
      messages: messages,
      lastMessageTimestamp: lastMessageTimestamp,
      loading: conversationData.loading || false,
      progress: conversationData.progress || '',
      thinkingType: (conversationData.thinkingType || type) as any,
      title: conversationData.title || '',
      lastMessageText: conversationData.lastMessageText || '',
      messageCount: conversationData.message_ids?.length || conversationData.messageCount || 0,
      createdAt: createdAt,
      updatedAt: updatedAt,
    };
    
    return conversation;
  } catch (error) {
    console.error(`Error fetching conversation ${conversationId}:`, error);
    throw error;
  }
};

/**
 * Sets up a listener for changes to a conversation.
 * 
 * @param userId - The ID of the user
 * @param conversationId - The ID of the conversation
 * @param type - The type of conversation ('reflection' or 'fraktal')
 * @param callback - Function to call when conversation changes
 * @returns Unsubscribe function to stop listening
 */
export const listenToConversation = (
  userId: string,
  conversationId: string,
  type: 'reflection' | 'fraktal',
  callback: (conversation: Conversation | null) => void
): (() => void) => {
  const conversationType = type === 'fraktal' ? 'fraktal_conversations' : 'reflection_conversations';
  const conversationPath = `users/${userId}/${conversationType}/${conversationId}`;
  const conversationRef = doc(db, conversationPath);
  
  const unsubscribe = onSnapshot(
    conversationRef,
    async (docSnap) => {
      if (docSnap.exists()) {
        // When the conversation document changes, fetch the full conversation with messages
        const conversation = await fetchConversationWithMessages(userId, conversationId, type);
        callback(conversation);
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error(`Error listening to conversation ${conversationId}:`, error);
      callback(null);
    }
  );
  
  return unsubscribe;
};

/**
 * Sets up a listener for changes to a conversation's messages.
 * 
 * @param userId - The ID of the user
 * @param conversationId - The ID of the conversation
 * @param type - The type of conversation ('reflection' | 'fraktal' | 'prompt-graph')
 * @param callback - Function to call when messages change
 * @returns Unsubscribe function to stop listening
 */
export const listenToConversationMessages = (
  userId: string,
  conversationId: string,
  type: 'reflection' | 'fraktal' | 'prompt-graph',
  callback: (messages: Message[]) => void
): (() => void) => {
  let conversationType: string;
  if (type === 'fraktal') {
    conversationType = 'fraktal_conversations';
  } else if (type === 'prompt-graph') {
    conversationType = 'graph_conversations';
  } else {
    conversationType = 'reflection_conversations';
  }
  const messagesPath = `users/${userId}/${conversationType}/${conversationId}/messages`;
  const messagesRef = collection(db, messagesPath);
  
  const unsubscribe = onSnapshot(
    messagesRef,
    (snapshot) => {
      if (!snapshot.empty) {
        const messages: Message[] = snapshot.docs.map(doc => {
          const data = doc.data() as FirebaseMessageData;
          
          // Determine thinking type - prefer modern thinkingType field
          let thinkingType: 'reflection' | 'fast-thinking' | 'user' | 'fraktal' | 'prompt-graph';
          
          if (data.thinkingType && 
              ['reflection', 'fast-thinking', 'user', 'fraktal', 'prompt-graph'].includes(data.thinkingType)) {
            // Use the modern thinkingType field directly
            thinkingType = data.thinkingType as 'reflection' | 'fast-thinking' | 'user' | 'fraktal' | 'prompt-graph';
          } else {
            // Fallback for legacy data
            const legacyData = data as any;
            
            // Enhanced fallback logic for prompt-graph conversations
            if (legacyData.process_type === 'fast-thinking') {
              thinkingType = 'fast-thinking';
            } else if (legacyData.process_type === 'user_input') {
              thinkingType = 'user';
            } else if (type === 'prompt-graph') {
              // For prompt-graph conversations, determine based on message pattern
              if (doc.id.includes('_response') && data.sender === 'bot') {
                // Bot responses in prompt-graph conversations could be from either microservice
                // Check if this is a follow-up (fast-thinking) or initial (prompt-graph)
                thinkingType = 'prompt-graph'; // Default to prompt-graph for bot responses
              } else {
                thinkingType = 'prompt-graph';
              }
            } else {
              // Default to the conversation type or 'reflection'
              thinkingType = type || 'reflection';
            }
          }
          
          // Get the createdAt value, either directly from property or by converting
          let createdAt: string;
          
          if (typeof data.createdAt === 'string') {
            createdAt = data.createdAt;
          } else if (data.createdAt) {
            createdAt = safeTimestampToISOString(data.createdAt);
          } else {
            throw new Error(`Message ${doc.id} is missing required field 'createdAt'`);
          }
          
          // Create a message with the improved structure
          const message: Message = {
            id: doc.id,
            sender: data.sender, // Use sender field directly (now required in FirebaseMessageData)
            text: data.text || data.input_text || '',
            createdAt: createdAt,
            conversationId: conversationId,
            thinkingType: thinkingType as any,
            loading: data.loading || false,
            error: data.error || false
          };
          
          // Copy attachments if they exist (documents and URLs)
          if (data.attachments && data.attachments.length > 0) {
            message.attachments = data.attachments;
          }
          
          // Copy extra_parameters if they exist (includes files_used, internal_nodes_used, etc.)
          if (data.extra_parameters) {
            message.extra_parameters = data.extra_parameters;
          }
          
          // Check for sein_chain_version at the root level and include it in extra_parameters
          const messageDataAny = data as any;
          if (messageDataAny.sein_chain_version) {
            // Initialize extra_parameters if it doesn't exist
            if (!message.extra_parameters) {
              message.extra_parameters = {};
            }
            
            // Copy the value to the extra_parameters object
            message.extra_parameters.sein_chain_version = messageDataAny.sein_chain_version;
          }
          
          // Enhance Sein-II messages to ensure proper display
          if (message.thinkingType === 'fraktal' && message.sender === 'bot') {
            const hasSeinIIIndicator = 
              (messageDataAny.sein_chain_version === 'SEIN_2') || 
              (message.extra_parameters?.sein_chain_version === 'SEIN_2') ||
              (message.extra_parameters?.fraktal_complexity === 'sein_complexity_2');

            if (hasSeinIIIndicator) {
              // Ensure extra_parameters exists
              if (!message.extra_parameters) {
                message.extra_parameters = {};
              }
              
              // Set both properties for maximum compatibility
              message.extra_parameters.sein_chain_version = 'SEIN_2';
              message.extra_parameters.fraktal_complexity = 'sein_complexity_2';
            }
          }
          
          return message;
        }).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        
        callback(messages);
      } else {
        callback([]);
      }
    },
    (error) => {
      console.error(`Error listening to messages for conversation ${conversationId}:`, error);
      callback([]);
    }
  );
  
  return unsubscribe;
};

/**
 * Sets up a listener for changes to a conversation's state.
 */
export const listenToConversationState = (
  userId: string,
  conversationId: string,
  type: 'reflection' | 'fraktal' | 'prompt-graph',
  callback: (state: { status: 'idle' | 'busy'; progress: string; type: 'reflection' | 'fraktal' | 'prompt-graph'; lastUpdated: string }) => void
): (() => void) => {
  let conversationType: string;
  if (type === 'fraktal') {
    conversationType = 'fraktal_conversations';
  } else if (type === 'prompt-graph') {
    conversationType = 'graph_conversations';
  } else {
    conversationType = 'reflection_conversations';
  }
  const conversationPath = `users/${userId}/${conversationType}/${conversationId}`;
  const conversationRef = doc(db, conversationPath);
  
  const unsubscribe = onSnapshot(
    conversationRef,
    (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as FirebaseConversationData;
        
        try {
          // Use helper function to convert timestamp
          const lastUpdated = data.updatedAt 
            ? safeTimestampToISOString(data.updatedAt)
            : new Date().toISOString();
          
          // Map loading to status - true → busy, false → idle
          const status = data.loading ? 'busy' as const : 'idle' as const;
          
          const state = {
            status: status,
            progress: data.progress || '',
            type: type,
            lastUpdated: lastUpdated
          };
          callback(state);
        } catch (error) {
          console.error(`Error processing conversation state:`, error);
        }
      }
    },
    (error) => {
      console.error(`Error listening to state for conversation ${conversationId}:`, error);
    }
  );
  
  return unsubscribe;
};

/**
 * Fetches only the metadata for all conversations belonging to a user.
 * This is more efficient than fetching all messages too.
 * Now supports pagination for improved performance.
 * 
 * @param userId - The ID of the user
 * @param pageSize - Number of conversations to fetch (default: 20)
 * @param lastDocumentSnapshot - Last document from previous page for cursor pagination
 * @returns Promise containing pagination result with conversations and cursor info
 */
export const fetchUserConversationsMetadata = async (
  userId: string, 
  pageSize = 20, 
  afterTimestamp?: string
): Promise<ConversationPaginationResult> => {
  try {
    // Build queries with timestamp-based pagination for both reflection and fraktal conversations
    const allConversations: Conversation[] = [];

    // 1. Fetch reflection conversations with timestamp-based pagination
    const reflectionConversationsRef = collection(db, `users/${userId}/reflection_conversations`);
    const reflectionQuery = query(
      reflectionConversationsRef,
      orderBy('lastMessageTimestamp', 'desc'),
      ...(afterTimestamp ? [where('lastMessageTimestamp', '<', afterTimestamp)] : []),
      limit(pageSize + 10) // Get a few extra to account for cross-collection merging
    );
    const reflectionSnapshot = await getDocs(reflectionQuery);
    
    for (const conversationDoc of reflectionSnapshot.docs) {
      const conversationId = conversationDoc.id;
      const conversationData = conversationDoc.data() as FirebaseConversationData;
      
      try {
        // Get message count from conversation metadata (maintained by backend)
        const messageCount = conversationData.messageCount || 0;
        
        // Convert timestamps safely
        const lastMessageTimestamp = safeTimestampToISOString(conversationData.lastMessageTimestamp);
        const createdAt = safeTimestampToISOString(conversationData.createdAt);
        const updatedAt = safeTimestampToISOString(conversationData.updatedAt);
        
        const conversation: Conversation = {
          id: conversationId,
          messages: [],
          lastMessageTimestamp,
          loading: conversationData.loading || false,
          progress: conversationData.progress || '',
          thinkingType: 'reflection',
          title: conversationData.title || '',
          lastMessageText: conversationData.lastMessageText || '',
          messageCount,
          createdAt,
          updatedAt,
        };
        
        allConversations.push(conversation);
      } catch (error) {
        console.error('Error processing reflection conversation', conversationId, ':', error);
        // Skip invalid conversations instead of throwing
        continue;
      }
    }

    // 2. Fetch fraktal conversations with timestamp-based pagination
    const fraktalConversationsRef = collection(db, `users/${userId}/fraktal_conversations`);
    const fraktalQuery = query(
      fraktalConversationsRef,
      orderBy('lastMessageTimestamp', 'desc'),
      ...(afterTimestamp ? [where('lastMessageTimestamp', '<', afterTimestamp)] : []),
      limit(pageSize + 10) // Get a few extra to account for cross-collection merging
    );
    const fraktalSnapshot = await getDocs(fraktalQuery);
    
    for (const conversationDoc of fraktalSnapshot.docs) {
      const conversationId = conversationDoc.id;
      const conversationData = conversationDoc.data() as FirebaseConversationData;
      
      try {
        // Get message count from conversation metadata (maintained by backend)
        const messageCount = conversationData.messageCount || 0;
        
        // Convert timestamps safely
        const lastMessageTimestamp = safeTimestampToISOString(conversationData.lastMessageTimestamp);
        const createdAt = safeTimestampToISOString(conversationData.createdAt);
        const updatedAt = safeTimestampToISOString(conversationData.updatedAt);
        
        const conversation: Conversation = {
          id: conversationId,
          messages: [],
          lastMessageTimestamp,
          loading: conversationData.loading || false,
          progress: conversationData.progress || '',
          thinkingType: 'fraktal',
          title: conversationData.title || '',
          lastMessageText: conversationData.lastMessageText || '',
          messageCount,
          createdAt,
          updatedAt,
        };
        
        allConversations.push(conversation);
      } catch (error) {
        console.error('Error processing fraktal conversation', conversationId, ':', error);
        // Skip invalid conversations instead of throwing
        continue;
      }
    }

    // 3. Fetch prompt-graph conversations with timestamp-based pagination
    const graphConversationsRef = collection(db, `users/${userId}/graph_conversations`);
    const graphQuery = query(
      graphConversationsRef,
      orderBy('lastMessageTimestamp', 'desc'),
      ...(afterTimestamp ? [where('lastMessageTimestamp', '<', afterTimestamp)] : []),
      limit(pageSize + 10) // Get a few extra to account for cross-collection merging
    );
    const graphSnapshot = await getDocs(graphQuery);
    
    for (const conversationDoc of graphSnapshot.docs) {
      const conversationId = conversationDoc.id;
      const conversationData = conversationDoc.data() as FirebaseConversationData;
      
      try {
        // Get message count from conversation metadata (maintained by backend)
        const messageCount = conversationData.messageCount || 0;
        
        // Convert timestamps safely
        const lastMessageTimestamp = safeTimestampToISOString(conversationData.lastMessageTimestamp);
        const createdAt = safeTimestampToISOString(conversationData.createdAt);
        const updatedAt = safeTimestampToISOString(conversationData.updatedAt);
        
        const conversation: Conversation = {
          id: conversationId,
          messages: [],
          lastMessageTimestamp,
          loading: conversationData.loading || false,
          progress: conversationData.progress || '',
          thinkingType: 'prompt-graph',
          title: conversationData.title || '',
          lastMessageText: conversationData.lastMessageText || '',
          messageCount,
          createdAt,
          updatedAt,
        };
        
        allConversations.push(conversation);
      } catch (error) {
        console.error('Error processing graph conversation', conversationId, ':', error);
        // Skip invalid conversations instead of throwing
        continue;
      }
    }

    // 4. Sort all conversations by timestamp and limit to requested page size
    const sortedConversations = allConversations.sort((a, b) => 
      new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime()
    );
    
    // Take only the requested page size
    const conversations = sortedConversations.slice(0, pageSize);
    
    // Determine if there are more conversations
    const hasMore = sortedConversations.length > pageSize;
    
    // Get the timestamp for the next page (last item's timestamp)
    const lastTimestamp = conversations.length > 0 ? conversations[conversations.length - 1].lastMessageTimestamp : undefined;
    
    return {
      conversations,
      hasMore,
      lastTimestamp
    };
  } catch (error) {
    console.error('Error fetching conversations metadata:', error);
    throw error;
  }
};

/**
 * Fetches more conversation metadata for pagination.
 * This is a convenience function for loading additional pages.
 * 
 * @param userId - The ID of the user
 * @param pageSize - Number of conversations to fetch (default: 20)
 * @param lastDocumentSnapshot - Last document from previous page
 * @returns Promise containing pagination result with next conversations
 */
export const fetchMoreUserConversationsMetadata = async (
  userId: string,
  pageSize = 20,
  afterTimestamp: string
): Promise<ConversationPaginationResult> => {
  return fetchUserConversationsMetadata(userId, pageSize, afterTimestamp);
};

/**
 * Sets up a listener for all new conversations for a user.
 * 
 * @param userId - The ID of the user 
 * @param callback - Function to call when conversations change
 * @param fromTimestamp - Optional timestamp to listen only to conversations newer than or equal to this timestamp
 * @returns Unsubscribe function to stop listening
 */
export const listenToUserConversations = (
  userId: string,
  callback: (conversations: Conversation[]) => void,
  fromTimestamp?: string
): (() => void) => {
  // console.log('Setting up listeners for user conversations:', userId);
  const conversations: Map<string, Conversation> = new Map();
  
  // Listen to reflection conversations
  const reflectionConversationsRef = collection(db, `users/${userId}/reflection_conversations`);
  const reflectionQuery = fromTimestamp 
    ? query(
        reflectionConversationsRef,
        orderBy('lastMessageTimestamp', 'desc'),
        where('lastMessageTimestamp', '>=', fromTimestamp)
      )
    : reflectionConversationsRef;
  const unsubscribeReflection = onSnapshot(reflectionQuery, async (snapshot) => {
    // console.log('Reflection conversations snapshot received, size:', snapshot.size);
    
    // Track deleted conversations
    const currentReflectionIds = snapshot.docs.map(doc => doc.id);
    const existingReflectionIds = Array.from(conversations.values())
      .filter(c => c.thinkingType === 'reflection')
      .map(c => c.id);
    
    // Handle deletions: remove conversations that no longer exist
    for (const id of existingReflectionIds) {
      if (!currentReflectionIds.includes(id)) {
        console.log(`Detected reflection conversation deletion: ${id}`);
        conversations.delete(id);
      }
    }
    
    // Process each conversation document
    for (const doc of snapshot.docs) {
      const conversationId = doc.id;
      const conversationData = doc.data() as FirebaseConversationData;
      
      // Convert timestamps
      let lastMessageTimestamp: string;
      let createdAt: string;
      let updatedAt: string;
      
      try {
        // Get lastMessageTimestamp
        if (conversationData.lastMessageTimestamp) {
          if (typeof conversationData.lastMessageTimestamp === 'string') {
            // Already a string timestamp, handle Firebase format
            lastMessageTimestamp = safeTimestampToISOString(conversationData.lastMessageTimestamp);
          } else {
            // Convert Firestore timestamp
            lastMessageTimestamp = safeTimestampToISOString(conversationData.lastMessageTimestamp);
          }
        } else {
          throw new Error(`Conversation ${conversationId} is missing lastMessageTimestamp`);
        }
        
        // Get createdAt
        if (conversationData.createdAt) {
          if (typeof conversationData.createdAt === 'string') {
            // Already a string timestamp, handle Firebase format
            createdAt = safeTimestampToISOString(conversationData.createdAt);
          } else {
            // Convert Firestore timestamp
            createdAt = safeTimestampToISOString(conversationData.createdAt);
          }
        } else {
          throw new Error(`Conversation ${conversationId} is missing createdAt`);
        }
        
        // Get updatedAt
        if (conversationData.updatedAt) {
          if (typeof conversationData.updatedAt === 'string') {
            // Already a string timestamp, handle Firebase format
            updatedAt = safeTimestampToISOString(conversationData.updatedAt);
          } else {
            // Convert Firestore timestamp
            updatedAt = safeTimestampToISOString(conversationData.updatedAt);
          }
        } else {
          throw new Error(`Conversation ${conversationId} is missing updatedAt`);
        }
      } catch (error) {
        console.error(`Error processing timestamps for conversation ${conversationId}:`, error);
        // Skip this conversation if timestamps are invalid
        continue;
      }
      
      // Get message count from conversation metadata (maintained by backend)
      const messageCount = conversationData.messageCount || 0;
      
      // Create or update conversation in the map
      const conversation: Conversation = {
        id: conversationId,
        messages: [],
        lastMessageTimestamp: lastMessageTimestamp,
        loading: conversationData.loading || false,
        progress: conversationData.progress || '',
        thinkingType: conversationData.thinkingType || 'reflection',
        title: conversationData.title || '',
        lastMessageText: conversationData.lastMessageText || '',
        messageCount: messageCount,
        createdAt: createdAt,
        updatedAt: updatedAt,
      };
      
      conversations.set(conversationId, conversation);
    }
    
    // Sort and call the callback with all conversations
    const sortedConversations = Array.from(conversations.values()).sort(
      (a, b) => new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime()
    );
    
    callback(sortedConversations);
  });
  
  // Listen to fraktal conversations 
  const fraktalConversationsRef = collection(db, `users/${userId}/fraktal_conversations`);
  const fraktalQuery = fromTimestamp 
    ? query(
        fraktalConversationsRef,
        orderBy('lastMessageTimestamp', 'desc'),
        where('lastMessageTimestamp', '>=', fromTimestamp)
      )
    : fraktalConversationsRef;
  const unsubscribeFraktal = onSnapshot(fraktalQuery, async (snapshot) => {
    // console.log('Fraktal conversations snapshot received, size:', snapshot.size);
    
    // Track deleted conversations
    const currentFraktalIds = snapshot.docs.map(doc => doc.id);
    const existingFraktalIds = Array.from(conversations.values())
      .filter(c => c.thinkingType === 'fraktal')
      .map(c => c.id);
    
    // Handle deletions: remove conversations that no longer exist
    for (const id of existingFraktalIds) {
      if (!currentFraktalIds.includes(id)) {
        console.log(`Detected fraktal conversation deletion: ${id}`);
        conversations.delete(id);
      }
    }
    
    // Process each conversation document
    for (const doc of snapshot.docs) {
      const conversationId = doc.id;
      const conversationData = doc.data() as FirebaseConversationData;
      
      // Convert timestamps
      let lastMessageTimestamp: string;
      let createdAt: string;
      let updatedAt: string;
      
      try {
        // Get lastMessageTimestamp
        if (conversationData.lastMessageTimestamp) {
          if (typeof conversationData.lastMessageTimestamp === 'string') {
            // Already a string timestamp, handle Firebase format
            lastMessageTimestamp = safeTimestampToISOString(conversationData.lastMessageTimestamp);
          } else {
            // Convert Firestore timestamp
            lastMessageTimestamp = safeTimestampToISOString(conversationData.lastMessageTimestamp);
          }
        } else {
          throw new Error(`Conversation ${conversationId} is missing lastMessageTimestamp`);
        }
        
        // Get createdAt
        if (conversationData.createdAt) {
          if (typeof conversationData.createdAt === 'string') {
            // Already a string timestamp, handle Firebase format
            createdAt = safeTimestampToISOString(conversationData.createdAt);
          } else {
            // Convert Firestore timestamp
            createdAt = safeTimestampToISOString(conversationData.createdAt);
          }
        } else {
          throw new Error(`Conversation ${conversationId} is missing createdAt`);
        }
        
        // Get updatedAt
        if (conversationData.updatedAt) {
          if (typeof conversationData.updatedAt === 'string') {
            // Already a string timestamp, handle Firebase format
            updatedAt = safeTimestampToISOString(conversationData.updatedAt);
          } else {
            // Convert Firestore timestamp
            updatedAt = safeTimestampToISOString(conversationData.updatedAt);
          }
        } else {
          throw new Error(`Conversation ${conversationId} is missing updatedAt`);
        }
      } catch (error) {
        console.error(`Error processing timestamps for conversation ${conversationId}:`, error);
        // Skip this conversation if timestamps are invalid
        continue;
      }
      
      // Get message count from conversation metadata (maintained by backend)
      const messageCount = conversationData.messageCount || 0;
      
      // Create or update conversation in the map
      const conversation: Conversation = {
        id: conversationId,
        messages: [],
        lastMessageTimestamp: lastMessageTimestamp,
        loading: conversationData.loading || false,
        progress: conversationData.progress || '',
        thinkingType: conversationData.thinkingType || 'fraktal', // Always fraktal for these conversations
        title: conversationData.title || '',
        lastMessageText: conversationData.lastMessageText || '',
        messageCount: messageCount,
        createdAt: createdAt,
        updatedAt: updatedAt,
      };
      
      conversations.set(conversationId, conversation);
    }
    
    // Sort and call the callback with all conversations
    const sortedConversations = Array.from(conversations.values()).sort(
      (a, b) => new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime()
    );
    
    callback(sortedConversations);
  });
  
  // Listen to prompt-graph conversations
  const graphConversationsRef = collection(db, `users/${userId}/graph_conversations`);
  const graphQuery = fromTimestamp 
    ? query(
        graphConversationsRef,
        orderBy('lastMessageTimestamp', 'desc'),
        where('lastMessageTimestamp', '>=', fromTimestamp)
      )
    : graphConversationsRef;
  const unsubscribeGraph = onSnapshot(graphQuery, async (snapshot) => {
    // console.log('Graph conversations snapshot received, size:', snapshot.size);
    
    // Track deleted conversations
    const currentGraphIds = snapshot.docs.map(doc => doc.id);
    const existingGraphIds = Array.from(conversations.values())
      .filter(c => c.thinkingType === 'prompt-graph')
      .map(c => c.id);
    
    // Handle deletions: remove conversations that no longer exist
    for (const id of existingGraphIds) {
      if (!currentGraphIds.includes(id)) {
        console.log(`Detected graph conversation deletion: ${id}`);
        conversations.delete(id);
      }
    }
    
    // Process each conversation document
    for (const doc of snapshot.docs) {
      const conversationId = doc.id;
      const conversationData = doc.data() as FirebaseConversationData;
      
      // Convert timestamps
      let lastMessageTimestamp: string;
      let createdAt: string;
      let updatedAt: string;
      
      try {
        // Get lastMessageTimestamp
        if (conversationData.lastMessageTimestamp) {
          if (typeof conversationData.lastMessageTimestamp === 'string') {
            // Already a string timestamp, handle Firebase format
            lastMessageTimestamp = safeTimestampToISOString(conversationData.lastMessageTimestamp);
          } else {
            // Convert Firestore timestamp
            lastMessageTimestamp = safeTimestampToISOString(conversationData.lastMessageTimestamp);
          }
        } else {
          throw new Error(`Conversation ${conversationId} is missing lastMessageTimestamp`);
        }
        
        // Get createdAt
        if (conversationData.createdAt) {
          if (typeof conversationData.createdAt === 'string') {
            // Already a string timestamp, handle Firebase format
            createdAt = safeTimestampToISOString(conversationData.createdAt);
          } else {
            // Convert Firestore timestamp
            createdAt = safeTimestampToISOString(conversationData.createdAt);
          }
        } else {
          throw new Error(`Conversation ${conversationId} is missing createdAt`);
        }
        
        // Get updatedAt
        if (conversationData.updatedAt) {
          if (typeof conversationData.updatedAt === 'string') {
            // Already a string timestamp, handle Firebase format
            updatedAt = safeTimestampToISOString(conversationData.updatedAt);
          } else {
            // Convert Firestore timestamp
            updatedAt = safeTimestampToISOString(conversationData.updatedAt);
          }
        } else {
          // Set updatedAt to createdAt if not available
          updatedAt = createdAt;
        }
      } catch (error) {
        console.error(`Error processing conversation ${conversationId}:`, error);
        continue; // Skip this conversation
      }

      // Get message count from conversation metadata (maintained by backend)
      const messageCount = conversationData.messageCount || 0;
      
      // Create or update conversation in the map
      const conversation: Conversation = {
        id: conversationId,
        messages: [],
        lastMessageTimestamp: lastMessageTimestamp,
        loading: conversationData.loading || false,
        progress: conversationData.progress || '',
        thinkingType: conversationData.thinkingType || 'prompt-graph',
        title: conversationData.title || '',
        lastMessageText: conversationData.lastMessageText || '',
        messageCount: messageCount,
        createdAt: createdAt,
        updatedAt: updatedAt,
      };
      
      conversations.set(conversationId, conversation);
    }
    
    // Sort and call the callback with all conversations
    const sortedConversations = Array.from(conversations.values()).sort(
      (a, b) => new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime()
    );
    
    callback(sortedConversations);
  });

  // Return a function to unsubscribe from all listeners
  return () => {
    unsubscribeReflection();
    unsubscribeFraktal();
    unsubscribeGraph();
  };
};

/**
 * Fetches only the messages for a specific conversation.
 * Call this when a user selects a conversation to view.
 * 
 * @param userId - The ID of the user
 * @param conversationId - The ID of the conversation
 * @param type - The type of conversation ('reflection' | 'fraktal' | 'prompt-graph')
 * @returns Promise containing an array of messages
 */
export const fetchConversationMessages = async (
  userId: string,
  conversationId: string,
  type: 'reflection' | 'fraktal' | 'prompt-graph'
): Promise<Message[]> => {
  try {
    let conversationType: string;
    if (type === 'fraktal') {
      conversationType = 'fraktal_conversations';
    } else if (type === 'prompt-graph') {
      conversationType = 'graph_conversations';
    } else {
      conversationType = 'reflection_conversations';
    }
    const messagesPath = `users/${userId}/${conversationType}/${conversationId}/messages`;
    const messagesRef = collection(db, messagesPath);
    const messagesSnapshot = await getDocs(messagesRef);
    
    if (messagesSnapshot.empty) {
      return [];
    }
    
    const messages: Message[] = messagesSnapshot.docs.map(messageDoc => {
      const messageData = messageDoc.data() as FirebaseMessageData;
      
      // Validate required fields
      if (!messageData.sender) {
        throw new Error(`Message ${messageDoc.id} is missing required field 'sender'`);
      }
      
      if (!messageData.text && !messageData.input_text) {
        throw new Error(`Message ${messageDoc.id} is missing required field 'text'`);
      }
      
      let thinkingType: 'reflection' | 'fast-thinking' | 'user' | 'fraktal' | 'prompt-graph';
      
      // Check if there's a thinkingType field directly in the document
      if (messageData.thinkingType && 
          ['reflection', 'fast-thinking', 'user', 'fraktal', 'prompt-graph'].includes(messageData.thinkingType)) {
        // Use the modern thinkingType field directly
        thinkingType = messageData.thinkingType as 'reflection' | 'fast-thinking' | 'user' | 'fraktal' | 'prompt-graph';
      } else {
        // Fallback for legacy data
        const legacyData = messageData as any;
        
        // Simple fallback logic without complex mapping
        if (legacyData.process_type === 'fast-thinking') {
          thinkingType = 'fast-thinking';
        } else if (legacyData.process_type === 'user_input') {
          thinkingType = 'user';
        } else {
          // Default to the conversation type or 'reflection'
          thinkingType = type || 'reflection';
        }
      }

      // Get the createdAt timestamp from the Firebase data
      // Based on the field name used in the frontend vs backend
      if (!messageData.createdAt) {
        throw new Error(`Message ${messageDoc.id} is missing required field 'createdAt'`);
      }
      
      // Convert Firebase timestamp to string if needed
      let createdAt: string;
      
      if (typeof messageData.createdAt === 'string') {
        createdAt = messageData.createdAt;
        // console.log('Using createdAt string directly:', createdAt);
      } else if (messageData.createdAt) {
        // For Firestore timestamp objects, convert using our helper
        createdAt = safeTimestampToISOString(messageData.createdAt);
      } else {
        throw new Error(`Message ${messageDoc.id} has invalid createdAt format`);
      }

      // Create a properly formatted message based on the new structure
      const message: Message = {
        id: messageDoc.id,
        text: messageData.text || messageData.input_text || '',
        createdAt: createdAt,
        conversationId: conversationId,
        thinkingType: thinkingType as any,
        sender: messageData.sender,
        loading: messageData.loading || false,
        error: messageData.error || false
      };
      
      // Copy attachments if they exist (documents and URLs)
      if (messageData.attachments && messageData.attachments.length > 0) {
        message.attachments = messageData.attachments;
      }
      
      // Add extra_parameters if they exist in the Firebase data
      if (messageData.extra_parameters) {
        message.extra_parameters = messageData.extra_parameters;
        // console.log('Added extra_parameters to message:', message.extra_parameters);
      }
      
      // Check for sein_chain_version at the root level and include it in extra_parameters
      const messageDataAny = messageData as any;
      if (messageDataAny.sein_chain_version) {
        // Initialize extra_parameters if it doesn't exist
        if (!message.extra_parameters) {
          message.extra_parameters = {};
        }
        
        // Copy the value to the extra_parameters object
        message.extra_parameters.sein_chain_version = messageDataAny.sein_chain_version;
      }
      
      // Enhance Sein-II messages to ensure proper display
      if (message.thinkingType === 'fraktal' && message.sender === 'bot') {
        const hasSeinIIIndicator = 
          (messageDataAny.sein_chain_version === 'SEIN_2') || 
          (message.extra_parameters?.sein_chain_version === 'SEIN_2') ||
          (message.extra_parameters?.fraktal_complexity === 'sein_complexity_2');

        if (hasSeinIIIndicator) {
          // Ensure extra_parameters exists
          if (!message.extra_parameters) {
            message.extra_parameters = {};
          }
          
          // Set both properties for maximum compatibility
          message.extra_parameters.sein_chain_version = 'SEIN_2';
          message.extra_parameters.fraktal_complexity = 'sein_complexity_2';
        }
      }
      
      return message;
    });
    
    // Sort messages chronologically
    const sortedMessages = messages.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    
    return sortedMessages;
  } catch (error) {
    console.error(`Error fetching messages for conversation ${conversationId}:`, error);
    console.error('Stack trace:', error instanceof Error ? error.stack : 'No stack trace available');
    throw error; // Re-throw the error instead of returning empty array
  }
};

/**
 * Deletes a conversation and all its messages from Firebase.
 * 
 * @param userId - The ID of the user
 * @param conversationId - The ID of the conversation to delete
 * @param type - The type of conversation ('reflection' | 'fraktal' | 'prompt-graph')
 * @returns Promise that resolves when deletion is complete
 */
export const deleteConversation = async (
  userId: string,
  conversationId: string,
  type: 'reflection' | 'fraktal' | 'prompt-graph'
): Promise<void> => {
  try {
    console.log(`Deleting conversation ${conversationId} of type ${type}`);
    let conversationType: string;
    if (type === 'fraktal') {
      conversationType = 'fraktal_conversations';
    } else if (type === 'prompt-graph') {
      conversationType = 'graph_conversations';
    } else {
      conversationType = 'reflection_conversations';
    }
    
    // Reference to the conversation document
    const conversationPath = `users/${userId}/${conversationType}/${conversationId}`;
    const conversationRef = doc(db, conversationPath);
    
    // Verify conversation exists before trying to delete
    const conversationDoc = await getDoc(conversationRef);
    if (!conversationDoc.exists()) {
      console.warn(`Conversation ${conversationId} does not exist, nothing to delete.`);
      return;
    }
    
    // Reference to the messages subcollection
    const messagesPath = `${conversationPath}/messages`;
    const messagesRef = collection(db, messagesPath);
    
    // 1. First delete all messages in the subcollection
    const messagesSnapshot = await getDocs(messagesRef);
    
    if (messagesSnapshot.empty) {
      console.log(`No messages found for conversation ${conversationId}`);
    } else {
      console.log(`Deleting ${messagesSnapshot.size} messages for conversation ${conversationId}`);
      
      // Extract all document IDs from all messages
      const allDocumentIds: string[] = [];
      messagesSnapshot.docs.forEach((messageDoc) => {
        const messageData = messageDoc.data();
        if (messageData.attachments && Array.isArray(messageData.attachments)) {
          messageData.attachments.forEach((attachment: any) => {
            if (attachment.type === 'document' && attachment.id) {
              allDocumentIds.push(attachment.id);
            }
          });
        }
      });
      
      // Mark all documents as deleted BEFORE deleting conversation
      if (allDocumentIds.length > 0) {
        try {
          const { markDocumentsAsDeleted } = await import('./documentUploadService');
          await markDocumentsAsDeleted(userId, allDocumentIds);
          console.log(`[ConversationService] Marked ${allDocumentIds.length} documents as deleted`);
        } catch (error) {
          console.error('[ConversationService] Failed to mark documents as deleted:', error);
          // Continue with conversation deletion even if document marking fails
        }
      }
      
      // Delete each message document
      const messageDeletions = messagesSnapshot.docs.map(async (messageDoc) => {
        try {
          const messageDocRef = doc(db, `${messagesPath}/${messageDoc.id}`);
          await deleteDoc(messageDocRef);
        } catch (error) {
          console.error(`Error deleting message ${messageDoc.id}:`, error);
          // Continue with other deletions even if one fails
        }
      });
      
      // Wait for all message deletions to complete
      await Promise.all(messageDeletions);
      console.log(`Successfully deleted messages for conversation ${conversationId}`);
    }
    
    // 2. Then delete the conversation document itself
    await deleteDoc(conversationRef);
    
    console.log(`Successfully deleted conversation ${conversationId}`);
    return;
  } catch (error) {
    console.error(`Error deleting conversation ${conversationId}:`, error);
    throw error;
  }
}; 